import React, { useState } from 'react';

// Components
import { Header, QuestionInput } from '../components/layout';
import { AvatarCard, ApproachesList, StepCard, Blackboard, QuestionDetailsCard, DiagramCard } from '../components/features';
import { LoadingState, ErrorState, NotFoundState, InitialState } from '../components/ui';

// Hooks
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { useTheme } from '../theme';

// Redux
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectCurrentQuestion,
  selectQuestionInput,
  selectApproaches,
  selectCurrentApproach,
  selectCurrentStep,
  selectCurrentApproachData,
  selectSteps,
  selectRelatedFormulas,
  selectFinalAnswer,
  selectVerification,
  selectQuestionExplanation,
  selectTopic,
  selectSubtopic,
  selectIsLoading,
  selectError,
  selectIsImageUploading,
  selectImageUrl,
  selectVisualElements,
  selectExtractedText,
  setQuestionInput,
  setCurrentApproach,
  setCurrentStep,
  fetchSolutionRequest,
  setImageUploading,
  fetchSolutionFromImageSuccess,
  fetchSolutionFromImageFailure,
} from '../store/slices/tutorSlice';
import { tutorApi } from '../services/api';
import { showToast } from '../utils/toast';

export const HomePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { branding } = useTheme();

  const [rightPanelView, setRightPanelView] = useState<'blackboard' | 'reference'>('blackboard');

  // Redux state
  const currentQuestion = useAppSelector(selectCurrentQuestion);
  const questionInput = useAppSelector(selectQuestionInput);
  const approaches = useAppSelector(selectApproaches);
  const currentApproach = useAppSelector(selectCurrentApproach);
  const currentStep = useAppSelector(selectCurrentStep);
  const currentApproachData = useAppSelector(selectCurrentApproachData);
  const steps = useAppSelector(selectSteps);
  const relatedFormulas = useAppSelector(selectRelatedFormulas);
  const finalAnswer = useAppSelector(selectFinalAnswer);
  const verification = useAppSelector(selectVerification);
  const questionExplanation = useAppSelector(selectQuestionExplanation);
  const topic = useAppSelector(selectTopic);
  const subtopic = useAppSelector(selectSubtopic);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const isImageUploading = useAppSelector(selectIsImageUploading);
  const imageUrl = useAppSelector(selectImageUrl);
  const visualElements = useAppSelector(selectVisualElements);
  const extractedText = useAppSelector(selectExtractedText);

  // Speech synthesis hook
  const { isSpeaking, speakStep, stopSpeech } = useSpeechSynthesis({
    steps,
    onStepChange: (step) => dispatch(setCurrentStep(step)),
  });

  // Handlers
  const handleApproachSelect = (index: number) => {
    stopSpeech();
    dispatch(setCurrentApproach(index));
  };

  const handleStepChange = (step: number) => {
    if (step >= 0 && step < steps.length) {
      dispatch(setCurrentStep(step));
    }
  };

  const handleAskQuestion = () => {
    const next = questionInput.trim();
    if (!next) return;
    stopSpeech();
    dispatch(fetchSolutionRequest(next));
  };

  const handleInputChange = (value: string) => {
    dispatch(setQuestionInput(value));
  };

  const handleImageUpload = async (file: File) => {
    stopSpeech();
    dispatch(setImageUploading(true));
    
    try {
      const response = await tutorApi.solveFromImage(file);
      dispatch(fetchSolutionFromImageSuccess(response));
      showToast.success('Question extracted and solved successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process image';
      console.error('Image solve API call failed:', errorMessage);
      dispatch(fetchSolutionFromImageFailure(errorMessage));
      showToast.error(errorMessage);
    }
  };

  // Image Upload Loading State
  if (isImageUploading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <LoadingState
          title="Processing your image..."
          message="Reading the question from your image and solving it"
        />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <LoadingState
          title="Analyzing your question..."
          message="Our AI is working on a step-by-step solution for you"
        />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <ErrorState
          title="Oops! Something went wrong"
          message={error}
        />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  // Initial State - no question asked yet
  if (!currentQuestion && approaches.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <InitialState
          title={`Welcome to ${branding.appTitle}`}
          message="Enter a math question below to get step-by-step solutions"
        />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  // Not Found State - question asked but no approaches returned
  if (currentQuestion && approaches.length === 0) {
    const message = finalAnswer?.trim()
      ? finalAnswer
      : "We couldn't find a solution for your question. Try rephrasing or ask a different question.";

    const title = finalAnswer?.trim() ? 'Message from Tutor' : 'No solution found';

    const verificationNote = verification?.trim() && verification !== 'N/A'
      ? `\n\nVerification: ${verification}`
      : '';

    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <NotFoundState
          title={title}
          message={`${message}${verificationNote}`}
        />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  // Safety check for currentApproachData
  if (!currentApproachData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <LoadingState />
        <QuestionInput
          questionInput={questionInput}
          onInputChange={handleInputChange}
          onSubmit={handleAskQuestion}
          onImageUpload={handleImageUpload}
          isImageUploading={isImageUploading}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="pt-4 pb-24 lg:pb-16">
        <div className="max-w-7xl mx-auto px-1">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            
            {/* Left Column - Avatar & Approaches (25%) */}
            <div className="lg:col-span-3 space-y-2 flex flex-col lg:h-[calc(100vh-160px)]">
              <AvatarCard
                isSpeaking={isSpeaking}
                onSpeak={() => speakStep(currentStep)}
                onStop={stopSpeech}
              />
              <QuestionDetailsCard
                topic={topic}
                subtopic={subtopic}
                questionExplanation={questionExplanation}
                showAbout={false}
              />
              <ApproachesList
                approaches={approaches}
                currentApproach={currentApproach}
                onSelectApproach={handleApproachSelect}
              />
            </div>

            {/* Middle Column - Teacher Explanation (42%) */}
            <div className="lg:col-span-5 space-y-2 flex flex-col lg:h-[calc(100vh-160px)]">
              <StepCard
                steps={steps}
                currentStep={currentStep}
                isSpeaking={isSpeaking}
                relatedFormulas={relatedFormulas}
                verification={verification}
                onStepChange={handleStepChange}
                onSpeak={() => speakStep(currentStep)}
              />
            </div>

            {/* Right Column - Toggle between Reference and Blackboard (33%) */}
            <div className="lg:col-span-4 lg:h-[calc(100vh-160px)] flex flex-col space-y-2">
              {visualElements && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex-none">
                  <div className="flex border-b border-blue-100 bg-blue-50">
                    <button
                      type="button"
                      onClick={() => setRightPanelView('blackboard')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        rightPanelView === 'blackboard'
                          ? 'text-blue-700 bg-white'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Blackboard
                    </button>
                    <button
                      type="button"
                      onClick={() => setRightPanelView('reference')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        rightPanelView === 'reference'
                          ? 'text-blue-700 bg-white'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Question Image
                    </button>
                  </div>
                </div>
              )}

              <div className="flex-1 min-h-0">
                {visualElements && imageUrl && rightPanelView === 'reference' ? (
                  <DiagramCard
                    imageUrl={imageUrl}
                    extractedText={extractedText}
                    visualElements={visualElements}
                  />
                ) : (
                  <Blackboard
                    steps={steps}
                    currentStep={currentStep}
                    onStepClick={handleStepChange}
                    currentQuestion={currentQuestion}
                    questionExplanation={questionExplanation}
                  />
                )}
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Question Input */}
      <QuestionInput
        questionInput={questionInput}
        onInputChange={handleInputChange}
        onSubmit={handleAskQuestion}
        onImageUpload={handleImageUpload}
        isImageUploading={isImageUploading}
      />
    </div>
  );
};

export default HomePage;
